import gsap from 'gsap';
import Swiper from 'swiper';
import { createApp, onMounted, onUnmounted, ref, watch } from 'vue';

import { useQuickView } from '~/composables/use-quick-view';
import { ANIMATION_CONFIGS } from '~/constants/animations';
import * as DataDogLogs from '~/lib/datadog/datadog';

import { useViewportSize } from '../composables/use-viewport-size';
import { BaseSection } from './base-section';

/**
 * Style Panel Section
 *
 * This section displays a main heading and below two or three images with a heading and a description
 *
 * @extends BaseSection
 * @constructor
 * @param {HTMLElement} container - The container element for the section
 */
class StylePanel extends BaseSection {
  constructor() {
    super('[data-section-type="style-panel"]');
  }

  init(instanceData) {
    const container = instanceData.container;
    const slides = container.querySelectorAll('[data-block-type="style-panel-slide"]');

    if (!container) {
      DataDogLogs.error(
        '[Allbirds section] - Style Panel - init - Container not found',
        {},
        new Error('Container not found'),
      );
      return;
    }

    if (!slides.length) {
      DataDogLogs.error(
        '[Allbirds section] - Style Panel - init - No slides',
        {},
        new Error('No slides'),
      );
      return;
    }

    const app = createApp({
      name: 'StylePanelSection',
      setup() {
        const carousel = ref(null);
        const quickViewRefs = ref({});
        const { isMobile } = useViewportSize();

        slides.forEach((slide) => {
          const productId = slide.dataset.productId;
          if (slide.dataset.enableQuickView === 'true' && productId) {
            quickViewRefs.value[productId] = useQuickView({
              closeOnClickOutside: slide.dataset.closeQuickViewOnClickOutside === 'true',
              closeOnEscapeKey: slide.dataset.closeQuickViewOnEscapeKey === 'true',
              id: productId,
              isOpenByDefault: slide.dataset.openQuickViewByDefault === 'true',
            });
          }
        });

        const getSwiperSettings = (slidesCount) => ({
          centeredSlides: isMobile.value && slidesCount > 2,
          enabled: slidesCount > 2,
          slidesPerView: isMobile.value && slidesCount > 2 ? 1.2 : slidesCount,
          spaceBetween: 10,
        });

        const shouldUseSwiper = (slidesCount) => {
          if (isMobile.value && slidesCount <= 2) return false;
          return slidesCount > 1;
        };

        onMounted(() => {
          if (shouldUseSwiper(slides.length)) {
            carousel.value = new Swiper(
              container.querySelector('.swiper'),
              getSwiperSettings(slides.length),
            );
          }
        });

        watch(isMobile, () => {
          carousel.value?.destroy();
          if (shouldUseSwiper(slides.length)) {
            carousel.value = new Swiper(
              container.querySelector('.swiper'),
              getSwiperSettings(slides.length),
            );
          }
        });

        onUnmounted(() => {
          carousel.value?.destroy();
        });

        return { quickViewRefs };
      },
    });
    app.mount(container);
  }

  /**
   * Sets up the animation timeline for the section that will play when it comes into view
   * This will be called automatically during initialization
   *
   * @param {Object} instanceData - The section instance data
   * @return {Object} The GSAP timeline for the animation
   */
  setupInViewAnimationTimeline(instanceData) {
    const container = instanceData.container;
    const animateItems = container.querySelectorAll('.animate-item');

    if (!container || !animateItems.length) {
      return null;
    }

    const tl = gsap.timeline({
      paused: true,
      scrollTrigger: {
        ...this.getInViewAnimationOptions(),
        trigger: container,
      },
    });

    if (!tl) {
      return null;
    }

    tl.to(animateItems, ANIMATION_CONFIGS.STAGGERED_FADE_IN, 0);

    return tl;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new StylePanel();
});
